import { useCheckProtectedResources } from '@/components/InvoiceReportDetailDrawer/hooks/useCheckProtectedResources';
import { useDrawerClose } from '@/context/Drawer';
import {
  useInvalidateReceivedInvoicesList,
  useReceivedInvoiceReconciliationCancelExclusion,
  useReceivedInvoiceReconciliationExclude,
  useReceivedInvoiceReconciliationSend,
  useReceivedInvoiceViewPermissionCheck,
} from '@/context/services/invoice/ReceivedInvoice.service';
import { NormalizedError } from '@/context/services/normalizeError';
import {
  ForbiddenCodeEnum,
  isAuthError,
  isForbiddenError,
} from '@/context/services/type';
import { useTranslation } from '@/i18n';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { toHtmlNode } from '@/utils/toHtmlNode';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback, useState } from 'react';

const TOAST_DURATION = 5;

type ReconciliationMutateResult =
  | { status: 'success' }
  | { status: 'error'; errors: NormalizedError };

// Shared by every 突合サービス連携 action (NA-5445/NA-5806/NA-5526). BE
// (ap_web#4598) returns the exact same uncoded 403 for "setting disabled"
// and "no longer has access" (e.g. observer, no PIC access) alike, so this
// re-checks whether the invoice is still viewable
// (useReceivedInvoiceViewPermissionCheck, via the same
// useCheckProtectedResources mechanism approve/reject reports use): still
// viewable → setting disabled → keep the drawer open; no longer viewable →
// close the drawer instead. Either way, the list may now show this row in a
// stale state, so it's refreshed regardless of outcome.
const useHandleReconciliationError = (id: string) => {
  const { t } = useTranslation();
  const notify = useToastAPI();
  const drawerClose = useDrawerClose();
  const invalidateList = useInvalidateReceivedInvoicesList();
  const { handleCheckPermission } = useCheckProtectedResources();
  const checkViewPermission = useReceivedInvoiceViewPermissionCheck(id);
  const notifyError = useCallback(
    (message: string) => {
      notify.error({ title: toHtmlNode(message), duration: TOAST_DURATION });
    },
    [notify]
  );

  const notifyFromResponse = useCallback(
    (errors: NormalizedError) => {
      notifyError(
        extractErrorMessage(
          errors,
          t,
          'received_invoice_reconciliation.error'
        ) ?? t('received_invoice_reconciliation_error_generic')
      );
    },
    [t, notifyError]
  );

  return useCallback(
    async (result: { status: 'error'; errors: NormalizedError }) => {
      const { statusCode, code } = result.errors;

      // Uncoded 401 — session/office access genuinely lost (logout, switch
      // office, delete/withdraw from office, "No Authority"): reload and
      // let Rails' own middleware redirect + flash, same as editing.
      if (isAuthError(statusCode)) {
        location.reload();
        return;
      }

      if (
        isForbiddenError(statusCode) &&
        code === ForbiddenCodeEnum.FORBIDDEN_MIMIC_MODE
      ) {
        notifyError(t('received_invoice_reconciliation_error_mimic'));
        return;
      }

      if (isForbiddenError(statusCode)) {
        await handleCheckPermission({
          fnCheckPermission: checkViewPermission,
          onCheckSuccess: () => notifyFromResponse(result.errors),
          onCheckFailure: () => {
            notifyFromResponse(result.errors);
            drawerClose();
          },
        });
        // The list may still show this row in a state (e.g. pending
        // reconciliation) that's no longer accurate now that permission was
        // lost, regardless of whether the drawer stayed open or closed.
        await invalidateList();
        return;
      }

      notifyFromResponse(result.errors);
    },
    [
      t,
      notifyError,
      notifyFromResponse,
      drawerClose,
      invalidateList,
      handleCheckPermission,
      checkViewPermission,
    ]
  );
};

export const useSendReconciliation = (id: string) => {
  const { mutateAsync } = useReceivedInvoiceReconciliationSend(id);
  const handleError = useHandleReconciliationError(id);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const handleConfirm = useCallback(async () => {
    setIsPending(true);
    const result = await mutateAsync();
    setIsPending(false);

    if (result.status === 'success') {
      closeModal();
      return;
    }

    await handleError(result);
  }, [mutateAsync, closeModal, handleError]);

  return { isModalOpen, isPending, openModal, closeModal, handleConfirm };
};

// Exclude/Cancel-exclusion have no confirmation popup (per NA-5806/NA-5526
// AC) — they mutate on click and only need pending state + the shared error
// handling above.
const useReconciliationStatusAction = (
  id: string,
  mutateAsync: () => Promise<ReconciliationMutateResult>
) => {
  const handleError = useHandleReconciliationError(id);
  const [isPending, setIsPending] = useState(false);

  const handleAction = useCallback(async () => {
    setIsPending(true);
    const result = await mutateAsync();
    setIsPending(false);

    if (result.status === 'error') {
      await handleError(result);
    }
  }, [mutateAsync, handleError]);

  return { isPending, handleAction };
};

export const useExcludeReconciliation = (id: string) => {
  const { mutateAsync } = useReceivedInvoiceReconciliationExclude(id);
  return useReconciliationStatusAction(id, mutateAsync);
};

export const useCancelExclusionReconciliation = (id: string) => {
  const { mutateAsync } = useReceivedInvoiceReconciliationCancelExclusion(id);
  return useReconciliationStatusAction(id, mutateAsync);
};
